import React, { memo, useState } from "react";
import { Divider, Stack, Typography } from "@mui/material";
import { LinkIcon, PenIcon } from "../images";
import { IconButton } from "../ui/IconButton";
import { Link } from "react-router-dom";
import { ReuseTooltip } from "../ui";
import { EditAccountDetailsModal } from "../ui/modal";
import { useAppSelector } from "../../hooks";

interface Props {
  name: string;
  size?: string;
  contactsImported: number | null;
  industry: string;
  campaign_id: string;
  product_url?: string;
  account_id: string;
  isIntegration: boolean;
  isDemo?: boolean;
}

export const CompanyInfo = memo(
  ({
    name,
    size,
    contactsImported,
    industry,
    campaign_id,
    product_url,
    account_id,
    isIntegration,
    isDemo,
  }: Props) => {
    const { isEditor } = useAppSelector((state) => state.company.userRoles);
    const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);

    const details = [
      {
        label: "Company Size",
        value: size === null ? "--" : `${size} employees`,
      },
      {
        label: "Contacts Imported",
        value: contactsImported ? `${contactsImported} contacts` : "--",
      },
      {
        label: "Industry",
        value: industry || "--",
      },
    ];

    return (
      <Stack
        gap="24px"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack flexDirection={"row"} gap={"10px"}>
          <Typography
            variant="h1"
            color="custom.textDarkBlue"
            sx={{
              fontSize: "42px",
              lineHeight: "56px",
              fontWeight: 600,
            }}
          >
            {name}
          </Typography>
          {!isDemo && (
            <Stack paddingTop={"12px"} flexDirection={"row"} gap={"10px"}>
              {product_url && (
                <Link to={`${product_url}`}>
                  <ReuseTooltip
                    text={`Visit: ${product_url}`}
                    hideChildren={false}
                  >
                    <IconButton
                      style={{ width: "32px", height: "32px" }}
                      icon={<LinkIcon />}
                    />
                  </ReuseTooltip>
                </Link>
              )}

              {!isIntegration && isEditor && (
                <IconButton
                  onClick={() => setIsOpenEditModal(true)}
                  style={{ width: "32px", height: "32px" }}
                  icon={<PenIcon />}
                />
              )}
            </Stack>
          )}
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" gap="24px">
            <Divider
              orientation="vertical"
              flexItem
              sx={{ alignSelf: "stretch" }}
            />
            {details.map(({ label, value }, index) => (
              <React.Fragment key={label}>
                <Stack gap="8px">
                  <Typography variant="body1" color="#1F293299" noWrap={true}>
                    {label}
                  </Typography>
                  <Typography
                    variant="h4"
                    color="custom.textDarkBlue"
                    sx={{
                      fontWeight: 500,
                      fontSize: "20px",
                      wordBreak: "break-word",
                      width: "max-content",
                      maxWidth: 320,
                    }}
                  >
                    {value}
                  </Typography>
                </Stack>
                {details.length - 1 !== index && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ alignSelf: "stretch" }}
                  />
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
        {isOpenEditModal && (
          <EditAccountDetailsModal
            account_id={account_id}
            campaign_id={campaign_id}
            accountWebsite={product_url}
            accountName={name}
            onClose={() => setIsOpenEditModal(false)}
          />
        )}
      </Stack>
    );
  },
);
